import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import './Abonnement.css';
import { supabase } from '../supabaseClient';

// Initialiser Stripe avec votre clé publique
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY || 'pk_test_placeholder');

// 🎯 OFFRES SIMPLIFIÉES
const PLANS = {
  essai: {
    id: 'essai',
    name: 'Découverte',
    description: 'Testez notre plateforme',
    price: 1,
    credits: 1,
    type: 'one_time',
    stripe_price_id: process.env.REACT_APP_STRIPE_PRICE_ESSAI, 
    features: [
      '1 cours de 30 minutes',
      'Accès à tous nos professeurs',
      'Sans engagement'
    ]
  },
  abonnement: {
    id: 'une_seance',
    name: 'Abonnement',
    description: 'Progressez chaque semaine',
    price: 44.99,
    originalPrice: 60,
    credits_par_semaine: 1,
    type: 'recurring',
    stripe_price_id: process.env.REACT_APP_STRIPE_PRICE_ABONNEMENT,
    features: [
      '1 séance par semaine',
      '30 minutes par séance',
      'Accès à tous nos professeurs'
    ]
  }
};

// Composant de paiement Stripe
function CheckoutForm({ selectedPlan, onSuccess, onError, loading, setLoading }) {
  const stripe = useStripe();
  const elements = useElements();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [errors, setErrors] = useState({});

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      // Validation
      if (!email || !name) {
        setErrors({ form: 'Veuillez remplir tous les champs' });
        setLoading(false);
        return;
      }

      const cardElement = elements.getElement(CardElement);

      // Créer le payment method
      const { error: paymentError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: name,
          email: email,
        },
      });

      if (paymentError) {
        setErrors({ payment: paymentError.message });
        setLoading(false);
        return;
      }

      // Appeler l'API appropriée selon le type de plan
      // ✅ APRÈS - Une seule route pour tout
const endpoint = '/api/stripe/create-subscription';

      const response = await fetch(`${process.env.REACT_APP_API_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${JSON.parse(localStorage.getItem('sb-aerhvxfzzpveykkedmej-auth-token') || '{}').access_token}`
        },
        body: JSON.stringify({
          payment_method_id: paymentMethod.id,
          price_id: selectedPlan.stripe_price_id,
          plan_type: selectedPlan.id,
          customer_email: email,
          customer_name: name
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Erreur lors du paiement');
      }

      // Gérer les cas spéciaux Stripe (3D Secure, etc.)
      if (result.requires_action) {
        const { error: confirmError } = await stripe.confirmCardPayment(result.client_secret);
        
        if (confirmError) {
          setErrors({ payment: confirmError.message });
          setLoading(false);
          return;
        }
      }

      onSuccess(result);

    } catch (err) {
      console.error('Erreur paiement:', err);
      setErrors({ form: err.message });
      onError(err);
    } finally {
      setLoading(false);
    }
  };

  const buttonText = selectedPlan.type === 'one_time' 
    ? `Payer ${selectedPlan.price}€`
    : `S'abonner - ${selectedPlan.price}€/mois`;

  return (
    <form onSubmit={handleSubmit} className="checkout-form">
      <div className="form-group">
        <label htmlFor="name">Nom complet</label>
        <input
          id="name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Votre nom complet"
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="email">Email</label>
        <input
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="votre@email.com"
          required
        />
      </div>

      <div className="form-group">
        <label>Informations de carte</label>
        <div className="card-element-container">
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#424770',
                  '::placeholder': {
                    color: '#aab7c4',
                  },
                },
                invalid: {
                  color: '#9e2146',
                },
              },
            }}
          />
        </div>
      </div>

      {errors.form && <div className="error-message">{errors.form}</div>}
      {errors.payment && <div className="error-message">{errors.payment}</div>}

      <button 
        type="submit" 
        disabled={!stripe || loading}
        className="subscribe-button"
      >
        {loading ? 'Traitement...' : buttonText}
      </button>
    </form>
  );
}

// Composant principal de la page d'abonnement
function Abonnement() {
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentSubscription, setCurrentSubscription] = useState(null);

  // Vérifier l'abonnement actuel
  useEffect(() => {
    checkCurrentSubscription();
  }, []);

  const checkCurrentSubscription = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/stripe/subscription-status`, {
        headers: {
          'Authorization': `Bearer ${JSON.parse(localStorage.getItem('sb-aerhvxfzzpveykkedmej-auth-token') || '{}').access_token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.has_subscription && data.abonnement?.status === 'active') {
  // Ne bloquer que si c'est un VRAI abonnement (pas un essai terminé)
  const isRealSubscription = data.abonnement.credits_par_semaine > 0;
  const hasCredits = data.abonnement.credits_disponibles > 0;
  
  // Bloquer seulement si abonnement actif avec recharge OU essai avec crédits restants
  if (isRealSubscription || hasCredits) {
    setCurrentSubscription(data.abonnement);
  }
}

      }
    } catch (error) {
      console.error('Erreur vérification abonnement:', error);
    }
  };

  const handleEssaiGratuit = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) { navigate('/login'); return; }
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/stripe/essai-gratuit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        navigate('/dashboard', { state: { message: 'Votre cours d\'essai gratuit est activé !', type: 'success' } });
      } else {
        alert(data.error || 'Erreur lors de l\'activation');
      }
    } catch(e) {
      alert('Erreur de connexion');
    }
  };

  const handlePlanSelect = (planKey) => {
    setSelectedPlan(PLANS[planKey]);
    setShowCheckout(true);
  };

  const handlePaymentSuccess = (result) => {
    const message = selectedPlan.type === 'one_time'
      ? 'Paiement réussi ! Votre crédit de cours est disponible.'
      : 'Abonnement activé avec succès ! Vous pouvez maintenant réserver vos cours.';
    
    navigate('/dashboard', { 
      state: { 
        message: message,
        type: 'success'
      }
    });
  };

  const handlePaymentError = (error) => {
    console.error('Erreur paiement:', error);
  };

  // Calculer le pourcentage de réduction
  const discountPercent = Math.round((1 - PLANS.abonnement.price / PLANS.abonnement.originalPrice) * 100);

  // Si l'utilisateur a déjà un abonnement actif
  if (currentSubscription) {
    return (
      <div className="abonnement-page">
        <div className="container">
          <div className="subscription-active">
            <h1>Votre abonnement actuel</h1>
            <div className="current-subscription-card">
              <h3>Abonnement FirstArabic</h3>
              <div className="subscription-details">
                <p><strong>Statut :</strong> Actif</p>
                <p><strong>Crédits disponibles :</strong> {currentSubscription.credits_disponibles}</p>
                <p><strong>Prix :</strong> 44,99€/mois</p>
              </div>
              <div className="subscription-actions">
                <button 
                  onClick={() => navigate('/dashboard')}
                  className="btn-primary"
                >
                  Aller au tableau de bord
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="abonnement-page">
      <div className="container">
        <div className="abonnement-header">
          <h1>Commencez votre apprentissage</h1>
          <p>Apprenez l'arabe avec nos professeurs qualifiés</p>
        </div>

        {!showCheckout ? (
          <div className="plans-section">
            <div className="plans-grid-simple">
              {/* Carte Découverte */}
              <div className="plan-card-simple">
                <div className="plan-badge">Découverte</div>
                <div className="plan-price-simple">
                  <span className="price-amount" style={{textDecoration:'line-through', color:'#aaa', fontSize:'1.2rem'}}>9,99€</span>
                  <span className="price-amount" style={{color:'#065f46', fontSize:'2rem', marginLeft:'8px'}}>1€</span>
                </div>
                <ul className="plan-features-simple">
                  {PLANS.essai.features.map((feature, index) => (
                    <li key={index}>✓ {feature}</li>
                  ))}
                </ul>
                <button 
                  onClick={() => handlePlanSelect('essai')}
                  className="btn-plan btn-essai"
                >
                  Essayer pour 1€
                </button>
              </div>

              {/* Carte Abonnement avec PROMO */}
              <div className="plan-card-simple featured">
                <div className="promo-ribbon">-{discountPercent}%</div>
                <div className="plan-badge popular">Offre limitée</div>
                <div className="plan-price-simple">
                  <span className="price-original">{PLANS.abonnement.originalPrice}€</span>
                  <span className="price-amount promo">{PLANS.abonnement.price}€</span>
                  <span className="price-period">/mois</span>
                </div>
                <ul className="plan-features-simple">
                  {PLANS.abonnement.features.map((feature, index) => (
                    <li key={index}>✓ {feature}</li>
                  ))}
                </ul>
                <button 
                  onClick={() => handlePlanSelect('abonnement')}
                  className="btn-plan btn-abonnement"
                >
                  En profiter
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="checkout-section">
            <div className="checkout-container">
              <div className="plan-summary">
                <h3>Récapitulatif</h3>
                <div className="selected-plan">
                  <h4>{selectedPlan.name}</h4>
                  {selectedPlan.originalPrice && (
                    <p className="selected-price-original">
                      <span className="strikethrough">{selectedPlan.originalPrice}€/mois</span>
                    </p>
                  )}
                  <p className="selected-price">
                    {selectedPlan.price}€{selectedPlan.type === 'recurring' ? '/mois' : ''}
                  </p>
                  {selectedPlan.originalPrice && (
                    <p className="savings-text">
                      Vous économisez {(selectedPlan.originalPrice - selectedPlan.price).toFixed(2)}€/mois
                    </p>
                  )}
                </div>
                
                <button 
                  onClick={() => setShowCheckout(false)}
                  className="btn-back"
                >
                  ← Changer d'offre
                </button>
              </div>

              <div className="payment-form">
                <h3>Informations de paiement</h3>
                <Elements stripe={stripePromise}>
                  <CheckoutForm
                    selectedPlan={selectedPlan}
                    onSuccess={handlePaymentSuccess}
                    onError={handlePaymentError}
                    loading={loading}
                    setLoading={setLoading}
                  />
                </Elements>
              </div>
            </div>
          </div>
        )}

        <div className="security-info">
          <p>🔒 Paiement sécurisé par Stripe</p>
        </div>
      </div>
    </div>
  );
}

export default Abonnement;
